import { useCoordination } from '@/helpers/coordination'
import { computed } from 'vue'
import {
  useProjectArchitectCalculator,
  useProjectLandscapeArchitectCalculator,
  useProjectCivilEngineerCalculator,
  useProjectCivilEngineerExcavationCalculator,
  useProjectEngineerCalculator,
  useProjectElectricalCalculator,
  useProjectHeatingCoolingCalculator,
  useProjectVentilationACCalculator,
  useProjectHVACCalculator,
  useProjectSanitaryCalculator,
  useProjectBuildingAutomationCalculator,
  useProjectSpecialistCoordinationCalculator,
} from '@/stores/projectCenter/projectServiceCalculatorFactory'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export type UsageSet = 'standard' | 'landscape'

/**
 * VAL-160: building type reference values (overrides) exist per role. For each
 * usage this reports the active roles whose calculation falls back to the
 * reference values of the usage category. The flags come from the socket with
 * the minutes-per-square-metre result, so they follow the pinned parameters.
 */
export function useCategoryReferenceHints(usageSet: () => UsageSet) {
  type HintStore = { setupData: unknown; categoryReferenceForUsages: boolean[] }
  const calculators: Array<{ role: Role; set: UsageSet; store: HintStore }> = [
    { role: 'ARCHITECT', set: 'standard', store: useProjectArchitectCalculator() },
    { role: 'LANDSCAPE_ARCHITECT', set: 'landscape', store: useProjectLandscapeArchitectCalculator() },
    { role: 'CIVIL_ENGINEER', set: 'standard', store: useProjectCivilEngineerCalculator() },
    { role: 'CIVIL_ENGINEER_EXCAVATION', set: 'standard', store: useProjectCivilEngineerExcavationCalculator() },
    { role: 'ENGINEER', set: 'standard', store: useProjectEngineerCalculator() },
    { role: 'ELECTRICAL', set: 'standard', store: useProjectElectricalCalculator() },
    { role: 'HEATING_COOLING', set: 'standard', store: useProjectHeatingCoolingCalculator() },
    { role: 'VENTILATION_AC', set: 'standard', store: useProjectVentilationACCalculator() },
    { role: 'HVAC', set: 'standard', store: useProjectHVACCalculator() },
    { role: 'SANITARY', set: 'standard', store: useProjectSanitaryCalculator() },
    { role: 'BUILDING_AUTOMATION', set: 'standard', store: useProjectBuildingAutomationCalculator() },
    { role: 'SPECIALIST_COORDINATION', set: 'standard', store: useProjectSpecialistCoordinationCalculator() },
  ]

  // A role is part of the project once its Setup data has been initialised.
  const { enabled: coordinationEnabled } = useCoordination()
  const activeCalculators = computed(() =>
    calculators.filter(
      (entry) =>
        entry.store.setupData !== null && (entry.role !== 'SPECIALIST_COORDINATION' || !coordinationEnabled.value)
    )
  )
  const hasMultipleRoles = computed(() => activeCalculators.value.length > 1)

  /** Roles that use the category reference values for the usage at this index. */
  const rolesUsingCategoryReference = computed(
    () =>
      (index: number): Role[] =>
        activeCalculators.value
          .filter((entry) => entry.set === usageSet() && entry.store.categoryReferenceForUsages[index] === true)
          .map((entry) => entry.role)
  )

  return { hasMultipleRoles, rolesUsingCategoryReference }
}
